/**
 * Error handling utilities
 *
 * Provides functions for error type checking, creating errors from API responses,
 * and handling HTTP status codes with appropriate error mappings.
 */

import { ErrorCodes } from "./codes";
import {
  LingDatabaseError,
  AuthenticationError,
  ValidationError,
} from "./classes";

/**
 * Type guard to check if an error is a LingDatabaseError
 * @param error - The error to check
 * @returns True if the error is a LingDatabaseError instance
 */
export function isLingDatabaseError(
  error: unknown
): error is LingDatabaseError {
  return Boolean(
    error &&
      typeof error === "object" &&
      "isLingDatabaseError" in error &&
      (error as { isLingDatabaseError: boolean }).isLingDatabaseError === true
  );
}

/**
 * Creates an error instance from an API error response
 * @param response - The API error response
 * @returns A LingDatabaseError instance
 */
export function createErrorFromResponse(response: {
  success: false;
  error: { code: string; message: string };
}): LingDatabaseError {
  const code =
    (response.error.code as ErrorCodes) || ErrorCodes.EXTERNAL_API_ERROR;
  return new LingDatabaseError(code, response.error.message);
}

/**
 * Handles HTTP errors by converting status codes to appropriate error instances
 * @param status - The HTTP status code
 * @param statusText - The HTTP status text
 * @param responseBody - Optional response body with error details
 * @returns A specific error instance based on the status code
 */
export function handleHttpError(
  status: number,
  statusText: string,
  responseBody?: unknown
): LingDatabaseError {
  const getMessage = (message: string | string[] | undefined): string => {
    if (Array.isArray(message)) {
      return message.join(", ");
    }
    return message || "";
  };

  switch (status) {
    case 400:
      return new ValidationError(
        "request",
        getMessage(
          (responseBody as { message?: string | string[] })?.message
        ) || "Bad request"
      );
    case 401:
      return new AuthenticationError(
        getMessage(
          (responseBody as { message?: string | string[] })?.message
        ) || "Unauthorized"
      );
    case 403:
      return new LingDatabaseError(
        ErrorCodes.FORBIDDEN,
        getMessage(
          (responseBody as { message?: string | string[] })?.message
        ) || "Forbidden"
      );
    case 404:
      return new LingDatabaseError(
        ErrorCodes.RESOURCE_NOT_FOUND,
        getMessage(
          (responseBody as { message?: string | string[] })?.message
        ) || "Not found"
      );
    case 408:
      return new LingDatabaseError(ErrorCodes.TIMEOUT_ERROR, "Request timeout");
    case 409:
      // Idempotency guard rejection: the operation was already processed.
      // Callers treat DUPLICATE_ATTEMPT as success-equivalent and stop retrying.
      return new LingDatabaseError(
        ErrorCodes.DUPLICATE_ATTEMPT,
        getMessage(
          (responseBody as { error?: { message?: string } })?.error?.message
        ) || "Duplicate attempt"
      );
    case 429:
      return new LingDatabaseError(
        ErrorCodes.RATE_LIMIT_EXCEEDED,
        "Rate limit exceeded"
      );
    case 500:
      return new LingDatabaseError(
        ErrorCodes.INTERNAL_SERVER_ERROR,
        "Internal server error"
      );
    case 502:
    case 503:
    case 504:
      return new LingDatabaseError(
        ErrorCodes.SERVICE_UNAVAILABLE,
        "Service unavailable"
      );
    default:
      return new LingDatabaseError(
        ErrorCodes.EXTERNAL_API_ERROR,
        `HTTP ${status}: ${statusText}`
      );
  }
}
